import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils/cn";
import { useMountTransition } from "@/lib/utils/useMountTransition";
import { Spinner } from "./Spinner";

type Variant = "primary" | "secondary" | "outline" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

// 2026-08-18 interaction layer, strengthened 2026-08-18 (v2): primary/
// outline/ghost hover shifts onto the theme (soft purple tint, a real
// shadow-hover lift + -1px rise on primary) rather than a flat slate
// darken - secondary/danger keep their own semantic colors untouched
// (near-black / red are not part of the purple+pink identity, and danger
// must stay visually distinct as destructive) and only pick up the
// standardized transition timing + press feedback below. Every variant now
// has a real `active:` (pointer-down) state - v1 had none at all - a
// small, immediate scale-down that communicates "this just got pressed"
// before releasing back to the hover state on pointer-up.
const variantClasses: Record<Variant, string> = {
  primary:
    "bg-primary-700 text-white hover:-translate-y-px hover:bg-primary-800 hover:shadow-hover active:translate-y-0 active:scale-[0.98] active:bg-primary-900 active:shadow-none focus-visible:outline-primary-700",
  secondary:
    "bg-slate-900 text-white hover:-translate-y-px hover:bg-slate-800 active:translate-y-0 active:scale-[0.98] active:bg-slate-950 focus-visible:outline-slate-900",
  outline:
    "bg-white text-slate-700 border border-slate-300 hover:-translate-y-px hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 hover:shadow-sm active:translate-y-0 active:scale-[0.98] active:bg-primary-100 focus-visible:outline-slate-400",
  ghost:
    "bg-transparent text-slate-600 hover:bg-primary-50 hover:text-primary-700 active:scale-[0.98] active:bg-primary-100 focus-visible:outline-slate-400",
  danger:
    "bg-red-600 text-white hover:-translate-y-px hover:bg-red-700 hover:shadow-sm active:translate-y-0 active:scale-[0.98] active:bg-red-800 focus-visible:outline-red-600",
};

const sizeClasses: Record<Size, string> = {
  sm: "h-8 px-3 text-xs gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-11 px-5 text-sm gap-2",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", loading, fullWidth, disabled, children, ...props }, ref) => {
    // The spinner slot only exists in the DOM while loading is true or
    // briefly after it flips false (the exit transition's 150ms window) -
    // at rest (never loading) it renders nothing at all, so a button that
    // never shows a spinner is byte-for-byte the same width as before this
    // change. While it IS mounted, its own width/opacity animate rather
    // than popping in - that's what keeps the button from visibly jumping
    // the instant `loading` toggles, the bug this fixes.
    const { shouldRender: showSpinnerSlot, isVisible: spinnerVisible } = useMountTransition(Boolean(loading), 150);
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          "inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 ease-out",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
          "disabled:translate-y-0 disabled:scale-100 disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed",
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && "w-full",
          className,
        )}
        {...props}
      >
        {showSpinnerSlot && (
          <span
            className={cn(
              "grid shrink-0 overflow-hidden transition-all duration-150 ease-out",
              spinnerVisible ? "w-3.5 opacity-100" : "w-0 opacity-0",
            )}
            aria-hidden="true"
          >
            <Spinner className="h-3.5 w-3.5" />
          </span>
        )}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
